// components/CalendarModal.tsx
import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarPlus,
  FaCheck,
  FaClock,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import { CalendarEvent } from '../types/calendar';

interface CalendarModalProps {
  events: CalendarEvent[];
  onBack: () => void;
  onAddEvent: (event: Omit<CalendarEvent, 'id' | 'createdBy' | 'createdAt'>) => void;
  onUpdateEvent: (id: string, updates: Partial<CalendarEvent>) => void;
  onDeleteEvent: (id: string) => void;
}

export const CalendarModal = ({ 
  events, 
  onBack, 
  onAddEvent, 
  onUpdateEvent, 
  onDeleteEvent 
}: CalendarModalProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string>('');

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Generar días del calendario
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      
      const dateString = day.toISOString().split('T')[0];
      const dayEvents = events.filter(event => event.date === dateString);
      const isCurrentMonth = day.getMonth() === month;
      const isToday = day.toDateString() === today.toDateString();
      
      days.push({
        date: day,
        dateString,
        dayNumber: day.getDate(),
        events: dayEvents,
        isCurrentMonth,
        isToday
      });
    }
    
    return days;
  }, [currentDate, events]);

  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    return events.filter(event => event.date === selectedDate);
  }, [selectedDate, events]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (dateString: string) => {
    setSelectedDate(selectedDate === dateString ? '' : dateString);
  };

  const getPriorityColor = (priority: CalendarEvent['priority']) => {
    switch (priority) {
      case 'high': return 'bg-red-400';
      case 'medium': return 'bg-yellow-400';
      case 'low': return 'bg-green-400';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="p-4 h-full flex flex-col bg-gradient-to-br from-purple-50 to-pink-50">
      {/* Header */}
      <div className="rounded-2xl bg-white/20 backdrop-blur-md border-b border-white/10 shadow-lg">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onBack}
            className="p-3 bg-primary text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-colors shadow-md"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          
           <div className="text-center">
            <h2 className="text-2xl font-bold bg-secondary bg-clip-text text-transparent">
              Calendario
            </h2>
          </div>
        
          
          <div className="w-[52px]" />
        </div>

        {/* Navegación del mes */}
        <div className="flex items-center justify-between px-4 pb-4">
            
          <button
            onClick={goToPreviousMonth}
            className="p-2 bg-white/60 rounded-lg hover:bg-white/80 transition-colors"
          >
            <FaChevronLeft className="w-4 h-4" />
          </button>
          
          <h3 className="text-xl font-bold text-white/60">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          
          <button
            onClick={goToNextMonth}
            className="p-2 bg-white/60 rounded-lg hover:bg-white/80 transition-colors"
          >
            <FaChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Calendario */}
      <div className="flex-1 py-4 overflow-hidden">
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 h-full py-4">
          
          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-200 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Días del mes */}
          <div className="grid grid-cols-7 gap-1 flex-1">
            {calendarDays.map((day, index) => (
              <button
                key={index}
                onClick={() => day.isCurrentMonth && handleDayClick(day.dateString)}
                className={`relative p-2 rounded-lg transition-all duration-200 min-h-[60px] flex flex-col ${
                  !day.isCurrentMonth 
                    ? 'text-gray-400 cursor-default' 
                    : day.isToday
                    ? 'bg-gradient-to-r from-primary to-primary-hover text-white font-bold'
                    : selectedDate === day.dateString
                    ? 'bg-white/30 text-secondary'
                    : 'hover:bg-white/10 text-gray-200'
                }`}
              >
                <span className="text-sm">{day.dayNumber}</span>
                
                {/* Indicadores de eventos */}
                {day.events.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 justify-center">
                    {day.events.slice(0, 3).map((event, idx) => (
                      <div
                        key={idx}
                        className={`w-1.5 h-1.5 rounded-full ${getPriorityColor(event.priority)}`}
                      />
                    ))}
                    {day.events.length > 3 && (
                      <span className="text-xs text-gray-500">+{day.events.length - 3}</span>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Panel de eventos para fecha seleccionada */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/20 backdrop-blur-md border-t border-white/10 p-4 max-h-48 overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-secondary">
              {new Date(selectedDate).toLocaleDateString('es-ES', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </h4>
            <span className="text-sm text-gray-200">
              {selectedDateEvents.length} evento{selectedDateEvents.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="space-y-2">
            {selectedDateEvents.map(event => (
              <div 
                key={event.id}
                className="flex items-center gap-3 p-3 bg-white/10 rounded-xl border border-white/10"
              >
                <div className={`w-3 h-3 rounded-full ${getPriorityColor(event.priority)}`} />
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-secondary text-sm truncate">{event.title}</p>
                  {event.description && (
                    <p className="text-xs text-gray-100 truncate">{event.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-secondary mt-1">
                    <span className="capitalize">{event.type}</span>
                    {event.time && <span>• {event.time}</span>}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onUpdateEvent(event.id, { completed: !event.completed })}
                    className={`p-2 rounded-lg transition-colors ${
                      event.completed 
                        ? 'text-green-600 hover:bg-green-100' 
                        : 'text-gray-200 hover:bg-white/20'
                    }`}
                  >
                    {event.completed ? <FaCheck className="w-3 h-3" /> : <FaClock className="w-3 h-3" />}
                  </button>
                  
                  <button
                    onClick={() => onDeleteEvent(event.id)}
                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}

            {selectedDateEvents.length === 0 && (
              <div className="text-center py-4 text-gray-400">
                <FaCalendarPlus className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay eventos para esta fecha</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};